import { Component, Inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AdminService } from '../../services/AdminService/admin';
import { CountryEventTemplateResponse, TemplateScope } from '../../models/country-config.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';

export interface UpsertCountryEventTemplateDialogData {
  countryCode: string;
  /** Present when editing an existing row — scope and event type are then fixed, not editable:
   *  changing either would really be creating a different row, not updating this one. */
  existing?: CountryEventTemplateResponse;
  /** Which scope "New" was clicked under, so e.g. "Add fragment" doesn't open pre-set to DOCUMENT. */
  defaultScope?: TemplateScope;
}

@Component({
  selector: 'app-upsert-country-event-template-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    ErrorBanner,
  ],
  templateUrl: './upsert-country-event-template-dialog.html',
  styleUrl: './upsert-country-event-template-dialog.scss',
})
export class UpsertCountryEventTemplateDialog {
  readonly isEditing: boolean;
  readonly countryCode: string;

  scope: TemplateScope;
  eventType: string;
  template: string;
  description: string;

  isSubmitting = signal(false);
  errorMessage = signal('');

  constructor(
    private dialogRef: MatDialogRef<UpsertCountryEventTemplateDialog>,
    private admin: AdminService,
    @Inject(MAT_DIALOG_DATA) public data: UpsertCountryEventTemplateDialogData
  ) {
    this.countryCode = data.countryCode;
    this.isEditing = !!data.existing;
    this.scope = data.existing?.scope ?? data.defaultScope ?? 'DOCUMENT';
    this.eventType = data.existing && data.existing.eventType !== '*' ? data.existing.eventType : '';
    this.template = data.existing?.template ?? (this.scope === 'DOCUMENT' ? this.documentSkeleton() : '');
    this.description = data.existing?.description ?? '';
  }

  private documentSkeleton(): string {
    return '<Document>\n  <Events>{{events}}</Events>\n</Document>';
  }

  onScopeChange(): void {
    // Only nudge the starting text for a brand-new row — never overwrite what someone is editing.
    if (this.isEditing || this.template.trim()) {
      return;
    }
    this.template = this.scope === 'DOCUMENT' ? this.documentSkeleton() : '';
  }

  get canSubmit(): boolean {
    return !!this.template.trim();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.canSubmit) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set('');

    this.admin
      .upsertEventTemplate(this.countryCode, this.scope, this.scope === 'EVENT' ? this.eventType || undefined : undefined, {
        template: this.template,
        description: this.description || undefined,
      })
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.dialogRef.close(true);
        },
        error: (err: HttpErrorResponse) => {
          this.isSubmitting.set(false);
          this.errorMessage.set(extractErrorMessage(err, 'Failed to save the template.'));
        },
      });
  }
}
